import { appApiSlice } from "src/api/app-ApiSlice";
import { TAG_GET_DEVICES, TAG_GET_QUOTE, TAG_MEASUREMENT_SYSTEM } from "src/api/api-tags";
import { projectV2ApiSlice } from "src/api/v2/project-v2-ApiSlice";
import {
  ElectricalPropertyKey,
  isElectricalCheckboxKey,
  isElectricalSelectKey,
  isElectricalTextKey,
  WizardSelectKey,
} from "src/api/project-settings-keys";
import { ProjectSettingsElectrical } from "src/model/api/v2/data-contracts";

export interface SetElectricalPropertyRequest {
  projectId: number;
  key: ElectricalPropertyKey;
  value: number | string | boolean;
}

export interface SetWizardPropertyRequest {
  projectId: number;
  key: WizardSelectKey;
  value: number;
}

export interface SetMeasurementSystemRequest {
  projectId: number;
  measurementId: number;
}

export interface SetPlannerIconSetRequest {
  projectId: number;
  plannerIconSetId: number;
}

export interface SetProjectGeneralInfoRequest {
  projectId: number;
  generalInfo: string;
}

interface SetPropertyResponse {
  message: string;
}

function patchElectricalProperty(draft: ProjectSettingsElectrical, { key, value }: SetElectricalPropertyRequest) {
  if (isElectricalSelectKey(key)) {
    draft[key] = Number(value);
  } else if (isElectricalCheckboxKey(key)) {
    draft[key] = Boolean(value);
  } else if (isElectricalTextKey(key)) {
    draft[key] = value.toString();
  } else {
    draft[key] = Number(value);
  }
}

export const projectSettingsApiSlice = appApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    setElectricalProperty: builder.mutation<SetPropertyResponse, SetElectricalPropertyRequest>({
      query: ({ projectId, key, value }) => ({
        url: `/v1/project/set-electrical-project-properties`,
        method: 'POST',
        body: {
          project_id: projectId,
          [key]: typeof value === 'boolean' ? Number(value) : value,
        },
      }),
      async onQueryStarted(request, { dispatch, queryFulfilled }) {
        const patch = dispatch(
            projectV2ApiSlice.util.updateQueryData('getProjectSettings', request.projectId, (draft) => {
              patchElectricalProperty(draft.electrical, request);
            }),
        );
        queryFulfilled.catch(patch.undo);
      },
    }),
    setWizardProperty: builder.mutation<SetPropertyResponse, SetWizardPropertyRequest>({
      query: ({ projectId, key, value }) => ({
        url: `/v1/project/set-wizard-project-properties`,
        method: 'POST',
        body: {
          project_id: projectId,
          [key]: value,
        },
      }),
      async onQueryStarted({ projectId, key, value }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
            projectV2ApiSlice.util.updateQueryData('getProjectSettings', projectId, (draft) => {
              draft.wizard[key] = value;
            }),
        );
        queryFulfilled.catch(patch.undo);
      },
    }),
    setMeasurementSystem: builder.mutation<SetPropertyResponse, SetMeasurementSystemRequest>({
      query: ({ projectId, measurementId }) => ({
        url: `/v1/project/set-measurement-system`,
        method: 'POST',
        body: {
          project_id: projectId,
          measurement_id: measurementId,
        },
      }),
      invalidatesTags: [TAG_MEASUREMENT_SYSTEM],
      async onQueryStarted({ projectId, measurementId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
            projectV2ApiSlice.util.updateQueryData('getProject', projectId, (draft) => {
              draft.project.system_of_measurement_id = measurementId;
            }),
        );
        queryFulfilled.catch(patch.undo);
      },
    }),
    setPlannerIconSet: builder.mutation<SetPropertyResponse, SetPlannerIconSetRequest>({
      query: ({ projectId, plannerIconSetId }) => ({
        url: `/v1/project/set-planner-icon-set`,
        method: 'POST',
        body: {
          project_id: projectId,
          planner_icon_set_id: plannerIconSetId,
        },
      }),
      invalidatesTags: [TAG_GET_DEVICES],
      async onQueryStarted({ projectId, plannerIconSetId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
            projectV2ApiSlice.util.updateQueryData('getProject', projectId, (draft) => {
              draft.project.planner_icon_set_id = plannerIconSetId;
            }),
        );
        queryFulfilled.catch(patch.undo);
      },
    }),
    setProjectGeneralInfo: builder.mutation<string, SetProjectGeneralInfoRequest>({
      query: ({ projectId, generalInfo }) => ({
        url: `/v1/project/set-project-quote-properties`,
        method: 'POST',
        body: {
          project_id: projectId,
          general_info: generalInfo,
        },
      }),
      invalidatesTags: [TAG_GET_QUOTE],
      async onQueryStarted({ projectId, generalInfo }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
            projectV2ApiSlice.util.updateQueryData('getProject', projectId, (draft) => {
              draft.project.general_info = generalInfo;
            }),
        );
        queryFulfilled.catch(patch.undo);
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useSetElectricalPropertyMutation,
  useSetWizardPropertyMutation,
  useSetMeasurementSystemMutation,
  useSetPlannerIconSetMutation,
  useSetProjectGeneralInfoMutation,
} = projectSettingsApiSlice;
